const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const IST_TIMEZONE = "Asia/Kolkata";

const getIstParts = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: IST_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);
};

const getIstPart = (parts, type, fallback = "00") =>
  parts.find((part) => part.type === type)?.value || fallback;

export const getTodayDateInputValue = (value = new Date()) => {
  const parts = getIstParts(value);
  return `${getIstPart(parts, "year")}-${getIstPart(parts, "month")}-${getIstPart(parts, "day")}`;
};

export const getCurrentIstHour = (value = new Date()) => {
  const parts = getIstParts(value);
  return Number(getIstPart(parts, "hour", "0"));
};

export const getCurrentIstMinutes = (value = new Date()) => {
  const parts = getIstParts(value);
  const hour = Number(getIstPart(parts, "hour", "0"));
  const minute = Number(getIstPart(parts, "minute", "0"));
  return hour * 60 + minute;
};

export const getAttendanceRefreshDelayMs = (value = new Date()) => {
  const minutes = getCurrentIstMinutes(value);
  const morningStart = 9 * 60;
  const morningFastEnd = 10 * 60 + 30;
  const eveningFastStart = 18 * 60;
  const nightQuietStart = 21 * 60;

  if (minutes < morningStart || minutes >= nightQuietStart) return null;

  if (minutes >= eveningFastStart && minutes < nightQuietStart) {
    return 20 * 1000;
  }

  if (minutes >= morningStart && minutes < morningFastEnd) {
    return 5 * 60 * 1000;
  }

  return 5 * 60 * 1000;
};

export const isBeforeAttendanceAbsentCutoff = (value = new Date()) =>
  getCurrentIstMinutes(value) < 10 * 60 + 30;

export const getDateInputValue = (value) => {
  if (!value) return "";

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return getTodayDateInputValue(value);
  }

  const rawValue = String(value).trim();
  const dateOnlyMatch = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) return `${dateOnlyMatch[1]}-${dateOnlyMatch[2]}-${dateOnlyMatch[3]}`;

  const timestampMatch = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})[T\s]/);
  if (timestampMatch) {
    return getTodayDateInputValue(new Date(rawValue));
  }

  return "";
};

export const formatDisplayDate = (value, fallback = "—") => {
  const inputDate = getDateInputValue(value);
  if (!inputDate) return fallback;

  const [year, month, day] = inputDate.split("-").map(Number);
  if (!year || !month || !day) return fallback;

  return `${String(day).padStart(2, "0")} ${MONTHS[month - 1]} ${year}`;
};

export const formatDisplayDateTime = (value, fallback = "—") => {
  if (!value) return fallback;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    const hours = String(value.getHours()).padStart(2, "0");
    const minutes = String(value.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }

  const match = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/
  );

  if (!match) return fallback;

  const [, year, month, day, hours = "00", minutes = "00"] = match;
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};
